import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Plus, Users, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import ScriptureHeader from '../components/ScriptureHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MINISTRY_BANNER = 'https://static.prod-images.emergentagent.com/jobs/9bd4de2a-403c-4211-ba6a-7e7952c06d35/images/0f840a9da0fc7fdcf5f799cedc92d5d0049c007d763102e1d4304916e6d82131.png';

export default function EventsPage() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    image: ''
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events`, newEvent);
      toast.success('Event added successfully!');
      setShowAddForm(false);
      setNewEvent({ title: '', description: '', date: '', time: '', location: '', image: '' });
      fetchEvents();
    } catch (error) {
      toast.error('Failed to add event');
    }
  };

  const upcomingEvents = events.filter(e => new Date(e.date) >= new Date());
  const pastEvents = events.filter(e => new Date(e.date) < new Date());

  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="events-page">
      {/* Scripture Header */}
      <ScriptureHeader page="events" />

      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-charcoal mb-4 block">{t('events.subtitle')}</span>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">{t('events.title')}</h1>
            <p className="text-charcoal/70 max-w-2xl mx-auto">
              Join us for worship, fellowship, and community events. There's always something happening at Living Fruit Ministry!
            </p>
          </motion.div>

          {/* Add Event Button */}
          <div className="flex justify-end mb-8">
            <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
              <DialogTrigger asChild>
                <Button className="bg-burgundy hover:bg-purple text-white" data-testid="add-event-btn">
                  <Plus size={18} className="mr-2" /> Add Event
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">Add New Event</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddEvent} className="space-y-4 mt-4">
                  <div>
                    <Label>Event Title</Label>
                    <Input
                      value={newEvent.title}
                      onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                      required
                      data-testid="event-title-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={newEvent.date}
                        onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                        required
                        data-testid="event-date-input"
                      />
                    </div>
                    <div>
                      <Label>Time</Label>
                      <Input
                        value={newEvent.time}
                        onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                        placeholder="10:00 AM"
                        data-testid="event-time-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      value={newEvent.location}
                      onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                      placeholder="Main Campus"
                      data-testid="event-location-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={newEvent.description}
                      onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                      rows={3}
                      data-testid="event-description-input"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-burgundy hover:bg-purple" data-testid="submit-event-btn">
                    Add Event
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Upcoming Events */}
          <div className="mb-16">
            <h2 className="font-serif text-2xl text-charcoal mb-6">{t('events.upcoming')}</h2>
            {upcomingEvents.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {upcomingEvents.map((event, i) => (
                  <EventCard key={event.id} event={event} index={i} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-2xl border border-gold/10">
                <Calendar size={48} className="mx-auto text-charcoal/20 mb-4" />
                <p className="text-charcoal/50">{t('events.noEvents')}</p>
              </div>
            )}
          </div>

          {/* Past Events */}
          {pastEvents.length > 0 && (
            <div>
              <h2 className="font-serif text-2xl text-charcoal mb-6">{t('events.past')}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-70">
                {pastEvents.slice(0, 6).map((event, i) => (
                  <EventCard key={event.id} event={event} index={i} isPast />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function EventCard({ event, index, isPast = false }) {
  const { t } = useTranslation();
  const [showRsvp, setShowRsvp] = useState(false);
  const [rsvpData, setRsvpData] = useState({ name: '', email: '', phone: '', guests: 1, notes: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rsvpSuccess, setRsvpSuccess] = useState(false);
  const [rsvpCount, setRsvpCount] = useState(0);

  useEffect(() => {
    if (!isPast && event.id) {
      fetchRsvpCount();
    }
  }, [event.id, isPast]);

  const fetchRsvpCount = async () => {
    try {
      const response = await axios.get(`${API}/events/${event.id}/rsvp-count`);
      setRsvpCount(response.data.total_guests || 0);
    } catch (error) {
      // Silently fail
    }
  };

  const handleRsvp = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/events/${event.id}/rsvp`, {
        ...rsvpData,
        event_id: event.id
      });
      setRsvpSuccess(true);
      toast.success(t('events.alreadyRegistered'));
      fetchRsvpCount();
    } catch (error) {
      if (error.response?.data?.detail?.includes('Already')) {
        toast.info('Already registered for this event');
      } else {
        toast.error(t('common.error'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const closeDialog = () => {
    setShowRsvp(false);
    setRsvpSuccess(false);
    setRsvpData({ name: '', email: '', phone: '', guests: 1, notes: '' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`bg-white rounded-2xl overflow-hidden border border-gold/10 hover:border-gold/30 transition-all duration-300 ${!isPast && 'hover:-translate-y-1'}`}
      data-testid={`event-card-${index}`}
    >
      {event.image && (
        <div className="aspect-video overflow-hidden">
          <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-gold text-sm">
            <Calendar size={16} />
            <span>{formatDate(event.date)}</span>
            {event.time && <span>• {event.time}</span>}
          </div>
          {!isPast && rsvpCount > 0 && (
            <span className="text-xs text-charcoal/50 flex items-center gap-1">
              <Users size={12} /> {rsvpCount}
            </span>
          )}
        </div>
        <h3 className="font-serif text-lg text-charcoal mb-2">{event.title}</h3>
        <p className="text-charcoal/60 text-sm mb-4 line-clamp-2">{event.description}</p>
        {event.location && (
          <div className="flex items-center gap-2 text-sm text-charcoal/50 mb-4">
            <MapPin size={14} />
            <span>{event.location}</span>
          </div>
        )}
        
        {!isPast && (
          <Dialog open={showRsvp} onOpenChange={setShowRsvp}>
            <DialogTrigger asChild>
              <Button 
                className="w-full bg-burgundy hover:bg-purple text-white"
                data-testid={`rsvp-btn-${index}`}
              >
                {t('events.rsvp')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">
                  {rsvpSuccess ? t('events.alreadyRegistered') : t('events.rsvpTitle')}
                </DialogTitle>
              </DialogHeader>
              
              {rsvpSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-olive rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-white" />
                  </div>
                  <p className="text-charcoal/70 mb-4">You're registered for <strong>{event.title}</strong></p>
                  <Button onClick={closeDialog} className="bg-burgundy hover:bg-purple text-white">
                    {t('common.close')}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleRsvp} className="space-y-4 mt-4">
                  <div className="bg-cream p-4 rounded-xl mb-4">
                    <p className="font-medium text-charcoal">{event.title}</p>
                    <p className="text-sm text-charcoal/60">{formatDate(event.date)} {event.time && `at ${event.time}`}</p>
                  </div>
                  <div>
                    <Label>{t('common.name')}</Label>
                    <Input
                      value={rsvpData.name}
                      onChange={(e) => setRsvpData({...rsvpData, name: e.target.value})}
                      required
                      className="bg-cream"
                    />
                  </div>
                  <div>
                    <Label>{t('common.email')}</Label>
                    <Input
                      type="email"
                      value={rsvpData.email}
                      onChange={(e) => setRsvpData({...rsvpData, email: e.target.value})}
                      required
                      className="bg-cream"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('common.phone')}</Label>
                      <Input
                        type="tel"
                        value={rsvpData.phone}
                        onChange={(e) => setRsvpData({...rsvpData, phone: e.target.value})}
                        className="bg-cream"
                      />
                    </div>
                    <div>
                      <Label>{t('events.guests')}</Label>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={rsvpData.guests}
                        onChange={(e) => setRsvpData({...rsvpData, guests: parseInt(e.target.value)})}
                        className="bg-cream"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>{t('events.notes')}</Label>
                    <Textarea
                      value={rsvpData.notes}
                      onChange={(e) => setRsvpData({...rsvpData, notes: e.target.value})}
                      rows={2}
                      className="bg-cream"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-burgundy hover:bg-purple text-white"
                  >
                    {isSubmitting ? t('common.loading') : t('common.register')}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>
    </motion.div>
  );
}
